import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ReportsViewer = ({ reports, onViewReport, selectedReport }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [selectedReports, setSelectedReports] = useState([]);

  const filteredReports = reports?.filter(report => {
    const matchesSearch = report?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         report?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || report?.status === statusFilter;
    const matchesType = typeFilter === 'all' || report?.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  })?.sort((a, b) => {
    if (sortBy === 'createdAt') return new Date(b?.createdAt) - new Date(a?.createdAt);
    if (sortBy === 'name') return a?.name?.localeCompare(b?.name);
    if (sortBy === 'completeness') return b?.completeness - a?.completeness;
    return 0;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'under-review': return 'bg-yellow-100 text-yellow-800';
      case 'flagged': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'under-review': return 'Clock';
      case 'flagged': return 'AlertTriangle';
      default: return 'FileText';
    }
  };

  const formatFileSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports(prev => 
      prev?.includes(reportId) 
        ? prev?.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleBulkExport = () => {
    console.log('Bulk exporting reports:', selectedReports);
    // Simulate bulk export
  };

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'eu-directive-standard', label: 'EU Directive Standard' },
    { value: 'germany-entgelttransparenz', label: 'Germany Entgelttransparenz' },
    { value: 'equity-analysis', label: 'Equity Analysis' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'verified', label: 'Verified' },
    { value: 'under-review', label: 'Under Review' },
    { value: 'flagged', label: 'Flagged' }
  ];

  const sortOptions = [
    { value: 'createdAt', label: 'Date Created' },
    { value: 'name', label: 'Name' },
    { value: 'completeness', label: 'Completeness' }
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search reports, departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 lg:flex lg:space-x-4">
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e?.target?.value)}
              options={typeOptions}
              className="min-w-[140px]"
            />
            
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              options={statusOptions}
              className="min-w-[140px]"
            />
            
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              options={sortOptions}
              className="min-w-[140px]"
            />
          </div>
        </div>
        
        {selectedReports?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {selectedReports?.length} reports selected
              </p>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkExport}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export Selected
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedReports([])}
                  iconName="X"
                  iconPosition="left"
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Reports Grid */}
      <div className="grid gap-6">
        {filteredReports?.map((report) => (
          <div 
            key={report?.id}
            className={`bg-card border rounded-lg p-6 transition-all hover:shadow-md ${
              selectedReport?.id === report?.id ? 'ring-2 ring-primary border-primary' : 'border-border'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <input
                  type="checkbox"
                  checked={selectedReports?.includes(report?.id)}
                  onChange={() => handleSelectReport(report?.id)}
                  className="mt-1 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                />
                
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {report?.name}
                      </h3>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Type</p>
                          <p className="text-sm font-medium text-foreground">
                            {report?.type?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Period</p>
                          <p className="text-sm font-medium text-foreground">
                            {report?.period?.toUpperCase()}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Department</p>
                          <p className="text-sm font-medium text-foreground">
                            {report?.department}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Data Points</p>
                          <p className="text-sm font-medium text-foreground">
                            {report?.dataPoints?.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-6 text-xs text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="Calendar" size={12} />
                          <span>{new Date(report?.createdAt)?.toLocaleDateString()}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <Icon name="HardDrive" size={12} />
                          <span>{formatFileSize(report?.fileSize)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <Icon name="User" size={12} />
                          <span>{report?.submittedBy}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <Icon name="Percent" size={12} />
                          <span>{report?.completeness}% complete</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end space-y-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report?.status)}`}>
                        <Icon name={getStatusIcon(report?.status)} size={12} className="mr-1" />
                        {report?.status?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </span>
                      
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => onViewReport?.(report)}
                          iconName="Eye"
                          iconPosition="left"
                        >
                          View
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          iconName="Download"
                          iconPosition="left"
                        >
                          Export
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Integrity Verification */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Icon name="Shield" size={16} className="text-green-500" />
                        <span className="text-sm font-medium text-green-600">Integrity Verified</span>
                        <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {report?.checksum?.substring(0, 16)}...
                        </span>
                      </div>
                      
                      {report?.verifiedBy && (
                        <span className="text-xs text-muted-foreground">
                          Verified by: {report?.verifiedBy}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredReports?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Reports Found</h3>
          <p className="text-muted-foreground">
            No reports match your current filter criteria. Try adjusting your search or filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportsViewer;